import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';

import { formatSignedPoint } from '../common/resultViewModel';

const buildPredictionLabel = (results) => {
  const labels = results
    .map((result) => result.label)
    .filter(Boolean);

  return labels.length > 0 ? labels.join(' / ') : null;
};

const buildRankDisplay = (ranking) => {
  const currentRank = Number(ranking?.currentUserDisplayRank);
  if (!Number.isFinite(currentRank) || currentRank <= 0) {
    return null;
  }

  const previousRank = Number(ranking?.currentUserBeforeRank);
  if (!Number.isFinite(previousRank) || previousRank <= 0) {
    return {
      color: 'text.secondary',
      detail: null,
      main: `${currentRank}位`,
    };
  }

  const change = previousRank - currentRank;
  if (change === 0) {
    return {
      color: 'text.secondary',
      detail: '変動なし',
      main: `${currentRank}位`,
    };
  }

  return {
    color: change > 0 ? 'success.main' : 'error.main',
    detail: `${change > 0 ? '↑' : '↓'}${Math.abs(change)}`,
    main: `${previousRank}位 → ${currentRank}位`,
  };
};

function PredictionBlock({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={800} sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" component="p" fontWeight={900} sx={{ overflowWrap: 'anywhere', lineHeight: 1.35 }}>
        {value || '表示できません'}
      </Typography>
    </Box>
  );
}

function MissResultContentSP({ viewModel }) {
  const { ranking, resultSelection, selections, summary } = viewModel;
  const predictionLabel = buildPredictionLabel(selections);
  const correctLabel = resultSelection?.label || null;
  const rankDisplay = buildRankDisplay(ranking);

  return (
    <Box sx={{ px: 1.25, py: 1.5, width: '100%', maxWidth: 360, mx: 'auto' }}>
      <Stack spacing={1.75}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" component="h3" fontWeight={900} sx={{ color: 'text.primary', mb: 0.75 }}>
            今回は予想が外れました
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.light',
            bgcolor: 'rgba(211, 47, 47, 0.06)',
            p: 1.25,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={700}>
            今回の収支
          </Typography>
          <Typography variant="h4" component="p" fontWeight={900} sx={{ color: summary.balance >= 0 ? 'success.main' : 'error.main', overflowWrap: 'anywhere' }}>
            {formatSignedPoint(summary.balance)}pt
          </Typography>
        </Box>

        <Stack spacing={1.1}>
          <PredictionBlock label="あなたの予想" value={predictionLabel} />
          <PredictionBlock label="的中した選択肢" value={correctLabel} />
        </Stack>

        <Divider />

        {rankDisplay ? (
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={800} sx={{ mb: 0.5 }}>
              ルーム内順位
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="h6" fontWeight={900}>
                {rankDisplay.main}
              </Typography>
              {rankDisplay.detail ? (
                <Typography variant="body1" fontWeight={900} sx={{ color: rankDisplay.color }}>
                  {rankDisplay.detail}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

export default MissResultContentSP;
